import React, { useCallback, useMemo } from 'react';
import { ClaimTabProps } from '../claim-registration';
import { useNavigate, useParams } from 'react-router';
import { RowClickedEvent } from 'ag-grid-community';
import ExcelTable from 'components/ui/excel-table';
import ClaimBinderDetails from '../claim-binder-details';
import {
  ExcelTableColDef,
  EExcelTablePaginationType,
  ECellValueDisplayMode,
  ECellValueType,
} from 'components/ui/excel-table/types';
import usePagination from 'hooks/usePagination';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { binderClaimApi } from 'services/apis';
import { useTranslation } from 'react-i18next';
import { BinderClaimResponseDto } from 'libs/pinnacle-sdk';
import { ENavigationPath } from 'constants/path';

export type BinderClaimSummaryRow = {
  key: string;
  claimNo?: string;
  legacyClaimNo?: string;
  binder?: string;
  reinsured?: string;
  broker?: string;
  currency?: string;
  shareOfPremiumSigned?: number;
  incurred?: number;
  payments?: number;
  outstanding?: number;
  claimStatus?: string;
  claimId: string;
  contractId?: string;
};

const BinderClaimsSummary: React.FC<ClaimTabProps> = ({
  claim,
  includeNext,
  includeBack,
  navigateNext,
  navigateBack,
}) => {
  const navigate = useNavigate();
  const { claimId: currentMasterClaimId } = useParams<{ claimId: string }>();
  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination(10);
  const { t } = useTranslation();

  const columnDefs: ExcelTableColDef<BinderClaimSummaryRow>[] = useMemo(
    () => [
      {
        headerName: t('claim.binderClaimsSummaryHeader.claimNo'),
        field: 'claimNo',
        width: 150,
        align: 'left',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.legacyClaimNo'),
        field: 'legacyClaimNo',
        width: 150,
        align: 'left',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.binder'),
        field: 'binder',
        width: 200,
        align: 'left',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.reinsured'),
        field: 'reinsured',
        width: 180,
        align: 'left',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.broker'),
        field: 'broker',
        width: 180,
        align: 'left',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.currency'),
        field: 'currency',
        width: 120,
        align: 'left',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        width: 200,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.incurred'),
        field: 'incurred',
        width: 200,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.payments'),
        field: 'payments',
        width: 200,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.outstanding'),
        field: 'outstanding',
        width: 200,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        headerName: t('claim.binderClaimsSummaryHeader.claimStatus'),
        field: 'claimStatus',
        width: 150,
      },
    ],
    [t]
  );

  const {
    data: { items = [], pagination: { total = 0 } = {} } = {},
    isLoading,
  } = useQuery({
    queryKey: [
      EQueryKeys.BINDER_CLAIMS,
      currentPage,
      pageSize,
      currentMasterClaimId || claim.claimId,
    ],
    queryFn: async () => {
      return binderClaimApi.filterBinderClaims({
        page: currentPage,
        pageSize,
        filterBinderClaimsQueryDto: {
          claims: [currentMasterClaimId || claim.claimId],
          includeClaim: true,
          includeContract: true,
          includeClaimHandler: false,
          includeSubmission: true,
        },
      });
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const handleRowClick = useCallback(
    (row: RowClickedEvent) => {
      navigate(
        ENavigationPath.CLAIM_DETAIL.replace(
          ':claimId',
          row.data.claimId
        ).replace(':tabId', '4') + `?claimNo=${row.data.claimNo}`
      );
    },
    [navigate]
  );

  const dataSource: BinderClaimSummaryRow[] = useMemo(() => {
    return items?.map((item: BinderClaimResponseDto) => {
      const claimId = item?.claimId || item?.claim?.claimId;
      const contractId = item?.contractId || item?.contract?.contractId;
      return {
        key: `${claimId}-${contractId}`,
        claimNo: item?.claimNo,
        legacyClaimNo: item?.claim?.legacyClaimNo,
        binder: item?.contract?.binder?.binderName,
        reinsured: item?.contract?.reinsured?.name,
        broker: item?.contract?.broker?.name,
        currency: item?.submission?.originalCurrency,
        shareOfPremiumSigned: item?.contract?.shareOfPremiumSigned
          ? Number(item.contract.shareOfPremiumSigned)
          : undefined,
        incurred: item?.incurredOurShare
          ? Number(item.incurredOurShare)
          : undefined,
        payments: item?.paymentsOurShare
          ? Number(item.paymentsOurShare)
          : undefined,
        outstanding: item?.outstandingOurShare
          ? Number(item.outstandingOurShare)
          : undefined,
        claimStatus: item?.claimStatus,
        claimId: claimId,
        contractId: contractId,
      } as BinderClaimSummaryRow;
    });
  }, [items]);

  return (
    <div className="p-6 bg-white rounded-lg">
      <div className="mb-4 text-xl font-semibold">
        {t('claim.navigation.binderClaimsSummary')}
      </div>
      <ExcelTable
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        columnDefs={columnDefs}
        data={dataSource}
        totalRows={total}
        loading={isLoading}
        paginationType={EExcelTablePaginationType.DETAIL}
        rowKeyColName="key"
        onRowClicked={handleRowClick}
        tableContainerClassName="h-[calc(100vh-250px)]"
      />
    </div>
  );
};

export default BinderClaimsSummary;
